import {
  AssetService,
  IAssetModel,
  useDataLoader,
  useExternalSources,
} from "@xala/common-services";
import {
  Button,
  Icon,
  InputSearch,
  ITableColumnProps,
  ITableFilter,
  NotificationService,
  PageContent,
  PageHeader,
  setTableColumnSearchProps,
  Table,
  Tag,
  Tooltip,
} from "@xala/common-ui";
import { Modal, Pagination } from "antd";
import { useTableDataProvider } from "../../../../helpers";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_ARRAY } from "../AssetForm";
import "./SelectAssetModal.scss";

const notificationService = NotificationService.getInstance();
const assetService = new AssetService().promisify();

const toNumberOrUndefined = (value: any | undefined) =>
  typeof value === "undefined" ? undefined : +value;

type SelectAssetsModalProps = {
  visible: boolean;
  onClose: () => void;
  onSelect: (assets: IAssetModel) => void;
}



export const SelectAssetsModal = ({ visible, onClose, onSelect }: SelectAssetsModalProps) => {
  const [t] = useTranslation();
  const sources = useExternalSources();

  const assetTypesLoader = useDataLoader({
    loader: () => assetService.getAssetTypes(),
    deps: [],
  });

  const assetTypes = useMemo(() => assetTypesLoader.data ?? [], [
    assetTypesLoader.data,
  ]);

  const {
    dataLoader: assetsLoader,
    filters,
    pagination,
    fullTextSearch,
    setFullTextSearch,
  } = useTableDataProvider({
    filtersSchema: {
      FullTextSearch: "string",
      Types: "strings",
      AgeRestrictionFrom: "number",
      AgeRestrictionTo: "number",
      IncludeImages: "boolean",
      Title: "string",
      ShortDescription: "string",
      IsPublished: "boolean",
      CreatedDateTimeFrom: "string",
      CreatedDateTimeTo: "string",
      AvailableFromFrom: "string",
      AvailableToTo: "string",
    },
    loader: (filters, pagination) => {
      return assetService.search({
        ...filters,
        ...pagination,
      });
    },
    deps: [],
    onError: (error) =>
      notificationService.error({
        message: t(
          "LOADING_DATA_ERROR_MESSAGE",
          "There was an error while loading data."
        ),
        description: error.Message,
      }),
  });

  const onTableChange = (_: any, incomingFilters: ITableFilter) =>
    filters.update((oldFilters) => ({
      ...oldFilters,
      ...incomingFilters,
      AgeRestrictionFrom: toNumberOrUndefined(
        incomingFilters.AssetAgeRestrictionValueMin?.[0]
      ),
      AgeRestrictionTo: toNumberOrUndefined(
        incomingFilters.AssetAgeRestrictionValueMin?.[1]
      ),
      CreatedDateTimeFrom: incomingFilters.CreationDate?.[0]?.toString(),
      CreatedDateTimeTo: incomingFilters.CreationDate?.[1]?.toString(),
    }));

  const onSearch = (value: string) =>
    filters.update((oldFilters) => ({
      ...oldFilters,
      FullTextSearch: value,
    }));

  const titleColumn: ITableColumnProps<IAssetModel> = {
    key: "Title",
    dataIndex: "Title",
    title: t("ASSETS_LIST_TABLE_TITLE_COLUMN"),
    width: "300px",
    ellipsis: true,
    filteredValue: filters.asTableArray.Title
      ? [filters.asTableArray.Title]
      : null,
    ...setTableColumnSearchProps(t("ASSETS_LIST_TABLE_TITLE_COLUMN")),
  };

  const publishStatusColumn: ITableColumnProps<IAssetModel> = {
    width: "160px",
    key: "IsPublished",
    dataIndex: "IsPublished",
    title: t("ASSETS_LIST_TABLE_PUBLISH_STATUS_COLUMN"),
    filters: STATUS_ARRAY.map((status) => ({
      text: status.Name,
      value: !status.Value,
    })),
    filteredValue: filters.asTableArray.IsPublished,
    render: (_, row: IAssetModel) => {
      const status = STATUS_ARRAY.find(
        (element) => element.Value === row?.IsDraft
      );

      return <p>{status?.Name}</p>;
    },
  };
  const typesColumn: ITableColumnProps<IAssetModel> = {
    key: "Types",
    dataIndex: "Types",
    align: "center",
    width: "180px",
    title: t("ASSETS_LIST_TABLE_ASSET_TYPE_CODE_COLUMN"),
    filters: assetTypes.map((assetType) => ({
      text: assetType.DisplayName,
      value: assetType.Code,
    })),
    filteredValue: filters.asTableArray.Types,
    render: (_, row: IAssetModel) => (
      <Tag
        colorRotate={assetTypes.findIndex(
          ({ Code }) => Code === row.AssetTypeCode
        )}
      >
        {row.AssetTypeDisplayName}
      </Tag>
    ),
  };

  const actionsColumn: ITableColumnProps<IAssetModel> = {
    key: "Actions",
    dataIndex: "Actions",
    align: "center",
    width: "100px",
    title: t("TABLE_ACTIONS_COLUMN", "Actions"),
    render: (_, asset: IAssetModel) => (
      <Button
        type="primary"
        title={t("BUTTON_SELECT", "Select")}
        onClick={() => onSelect(asset)}
      >
        {t("BUTTON_SELECT", "Select")}
      </Button>
    ),
  };


  let columns: Array<ITableColumnProps<IAssetModel>> = [
    titleColumn,
    publishStatusColumn,
    typesColumn,
    actionsColumn,
  ];



  return (
    <Modal visible={visible} onCancel={onClose} onOk={onClose} width={1000} maskClosable={true}>
      <PageContent footer={<Pagination {...pagination.props} />}>
        <PageHeader
          title="Select Asset"
          extra={
            <>
              <InputSearch
                key="search"
                placeholder="Search"
                value={fullTextSearch}
                onChange={({ target: { value } }) => setFullTextSearch(value)}
                onSearch={onSearch}
                style={{ width: 250 }}
                allowClear={true}
              />
              <Tooltip
                overlay={t("MENU_OPTION_CLEAR_FILTERS", "Clear filters")}
              >
                <Button
                  key="clear-filters"
                  shape="circle"
                  icon={<Icon type="clear" />}
                  onClick={filters.clear}
                  title={t("MENU_OPTION_CLEAR_FILTERS", "Clear filters")}
                />
              </Tooltip>
              <Button
                key="reload"
                shape="circle"
                icon={<Icon type="reload" />}
                onClick={assetsLoader.refresh}
                title="Refresh data"
              />
              {sources.map(({ SourceSystemAlias, SourceSystemConfig }) => (
                <Button
                  key="add"
                  shape="round"
                  type="primary"
                  icon={<Icon type="plus" />}
                  onClick={() =>
                    window.open(
                      `${SourceSystemConfig.BaseUri}${SourceSystemConfig.AddAsset}`
                    )
                  }
                  title={SourceSystemAlias}
                >
                  {SourceSystemAlias}
                </Button>
              ))}
            </>
          }
        />
        <Table<IAssetModel>
          rowKey="Id"
          columns={columns}
          dataSource={assetsLoader.data?.Entities}
          loading={assetsLoader.loading}
          pagination={false}
          onChange={onTableChange}
        />
      </PageContent>
    </Modal>
  );
};
